import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Flame } from "lucide-react";
import { DroneIcon } from "@/components/DroneIcon";

function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    // aggregate intensity by identical coordinates so repeat locations glow hotter
    const buckets = {};
    points.forEach((p) => {
      const key = `${p.lat.toFixed(3)},${p.lon.toFixed(3)}`;
      buckets[key] = buckets[key] ? { ...buckets[key], w: buckets[key].w + 1 } : { lat: p.lat, lon: p.lon, w: 1 };
    });
    const heatPts = Object.values(buckets).map((b) => [b.lat, b.lon, Math.min(1, 0.35 + b.w * 0.2)]);
    const layer = L.heatLayer(heatPts, {
      radius: 38, blur: 28, maxZoom: 12, minOpacity: 0.35,
      gradient: { 0.2: "#84CC16", 0.45: "#eab308", 0.7: "#f97316", 1: "#ef4444" },
    });
    layer.addTo(map);
    try { map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [60, 60], maxZoom: 10 }); } catch (err) { console.error("Heatmap fitBounds failed:", err); }
    return () => { map.removeLayer(layer); };
  }, [points, map]);
  return null;
}

export default function HeatmapPage({ fullscreen = false }) {
  const navigate = useNavigate();
  const [jobCount, setJobCount] = useState(0);
  const [points, setPoints] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | geocoding | ready | empty

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/jobs");
        const withLoc = r.data.filter((j) => j.location);
        if (!active) return;
        setJobCount(r.data.length);
        if (!withLoc.length) { setStatus("empty"); return; }
        setStatus("geocoding");
        const gr = await api.post("/geocode/batch", { locations: withLoc.map((j) => j.location) });
        const lookup = gr.data || {};
        const out = [];
        for (const j of withLoc) {
          const hit = lookup[j.location.trim()];
          if (hit && hit.found) out.push({ id: j.id, title: j.title, location: j.location, lat: hit.lat, lon: hit.lon });
        }
        if (!active) return;
        setPoints(out);
        setStatus(out.length ? "ready" : "empty");
      } catch (e) {
        if (active) setStatus("empty");
      }
    })();
    return () => { active = false; };
  }, []);

  const mapped = points.length;

  return (
    <div className={fullscreen ? "h-screen w-screen flex flex-col bg-slate-900" : "space-y-4 fade-up"}>
      <div className={fullscreen ? "flex items-center justify-between px-5 h-14 bg-slate-900 text-white shrink-0" : "flex items-center justify-between"}>
        <div className="flex items-center gap-2">
          {fullscreen && <DroneIcon className="h-6 w-6 text-lime-400" />}
          <Flame className={fullscreen ? "h-5 w-5 text-orange-400" : "h-6 w-6 text-orange-500"} />
          <h1 className={fullscreen ? "font-heading font-bold text-lg text-white" : "text-2xl font-bold font-heading text-slate-900"}>
            Jobs Heat Map <span className="text-lime-500">({jobCount} jobs · {mapped} mapped)</span>
          </h1>
        </div>
        {fullscreen
          ? <button onClick={() => window.close()} data-testid="heatmap-close-btn" className="text-slate-300 hover:text-white text-sm">Close</button>
          : <button onClick={() => navigate(-1)} data-testid="heatmap-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Back</button>}
      </div>
      {!fullscreen && <p className="text-sm text-slate-500">Every job we've done and have scheduled — brighter/red areas are our busiest work hotspots.</p>}
      {status === "loading" || status === "geocoding" ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="heatmap-loading">
          <Loader2 className="h-8 w-8 animate-spin text-lime-500" />
          <p className="text-sm text-slate-400">{status === "loading" ? "Loading jobs…" : "Mapping job locations…"}</p>
        </div>
      ) : status === "empty" ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="heatmap-empty">
          <Flame className="h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-400">No job locations could be mapped yet.</p>
        </div>
      ) : (
        <div className={fullscreen ? "flex-1" : "rounded-xl overflow-hidden border border-slate-200"} style={fullscreen ? {} : { height: "72vh" }} data-testid="heatmap-container">
          <MapContainer center={[points[0].lat, points[0].lon]} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <HeatLayer points={points} />
          </MapContainer>
        </div>
      )}
    </div>
  );
}
